<template>
  <transition-group
    name="slide"
    tag="div"
    class="public-navigation-menu"
    :class="mobileClasses"
    @click="$emit('click')"
  >
    <template v-if="isMobile">
      <component :is="keyToComponents[key]" v-for="{ key } in mobileBtns" :key="`${key}-btn`" />
    </template>
    <template v-else>
      <!-- reset button -->
      <NavBtnReset v-if="showResetBtn" key="reset-btn" />
      <!-- standalone buttons -->
      <component :is="keyToComponents[key]" v-for="{ key } in standaloneBtns" :key="`${key}-btn`" />
      <!-- visualisations button if >1 available visualisation types -->
      <template v-if="showVisualisationsBtn">
        <NavBtnVisualisations
          key="visualisations-btn"
          @click="visualisationsExpanded = !visualisationsExpanded"
        />
        <template v-if="visualisationsExpanded">
          <component
            :is="keyToComponents[key]"
            v-for="{ key } in visualisationBtns"
            :key="`${key}-btn`"
          />
        </template>
      </template>
      <!-- menu button if menu item buttons exist -->
      <template v-if="menuItemBtns.length > 0">
        <NavBtnMenu key="menu-btn" @click="menuExpanded = !menuExpanded" />
        <template v-if="menuExpanded">
          <component
            :is="keyToComponents[key]"
            v-for="{ key } in menuItemBtns"
            :key="`${key}-btn`"
          />
        </template>
      </template>
    </template>
  </transition-group>
</template>

<script lang="ts">
import Vue, { VueConstructor } from 'vue'
import Component from 'vue-class-component'

import { useNavButtonsStore } from '@/store/public/navButtons'
import NavBtnMenu from '@/components/public/NavButtons/NavBtnMenu.vue'
import NavBtnSearch from '@/components/public/NavButtons/NavBtnSearch.vue'
import NavBtnFaq from '@/components/public/NavButtons/NavBtnFaq.vue'
import NavBtnDownload from '@/components/public/NavButtons/NavBtnDownload.vue'
import NavBtnFilterByCategory from '@/components/public/NavButtons/NavBtnFilterByCategory.vue'
import NavBtnVisualisations from '@/components/public/NavButtons/NavBtnVisualisations.vue'
import { NavButtonKey, NavButtonKeys } from '@/types'
import NavBtnBubbles from '@/components/public/NavButtons/NavBtnBubbles.vue'
import NavBtnXYBubbles from '@/components/public/NavButtons/NavBtnXYBubbles.vue'
import NavBtnMap from '@/components/public/NavButtons/NavBtnMap.vue'
import NavBtnNestedBubbles from '@/components/public/NavButtons/NavBtnNestedBubbles.vue'
import NavBtnSunburst from '@/components/public/NavButtons/NavBtnSunburst.vue'
import NavBtnReset from '@/components/public/NavButtons/NavBtnReset.vue'

const components = {
  NavBtnMenu,
  NavBtnReset,
  NavBtnVisualisations,
}

const props = {
  isMobile: Boolean,
}

@Component({ components })
export default class NavButtons extends Vue.extend({ props }) {
  menuExpanded = false
  visualisationsExpanded = false
  navButtonsStore = useNavButtonsStore()

  get showResetBtn() {
    return this.navButtonsStore.buttonEnabled(NavButtonKeys.RESET)
  }

  get standaloneBtns() {
    return this.navButtonsStore.standaloneBtns
  }

  get menuItemBtns() {
    return this.navButtonsStore.menuItemBtns
  }

  get visualisationBtns() {
    return this.navButtonsStore.visualisationBtns
  }

  get mobileBtns() {
    return [...this.standaloneBtns, ...this.menuItemBtns].filter(
      ({ key }) => !(this.isMobile && key === NavButtonKeys.MAP),
    )
  }

  get activeVisualisationTypes() {
    return this.$typedStore.activeVisualisation.activeVisualisationTypes
  }

  get showVisualisationsBtn() {
    return this.activeVisualisationTypes.length > 1
  }

  get keyToComponents(): Record<NavButtonKey, VueConstructor> {
    return {
      [NavButtonKeys.SEARCH]: NavBtnSearch,
      [NavButtonKeys.INFO]: NavBtnFaq,
      [NavButtonKeys.DOWNLOAD]: NavBtnDownload,
      [NavButtonKeys.FILTER]: NavBtnFilterByCategory,
      [NavButtonKeys.BUBBLES]: NavBtnBubbles,
      [NavButtonKeys.XY_BUBBLES]: NavBtnXYBubbles,
      [NavButtonKeys.MAP]: NavBtnMap,
      [NavButtonKeys.NESTED_BUBBLES]: NavBtnNestedBubbles,
      [NavButtonKeys.SUNBURST]: NavBtnSunburst,
    }
  }

  get mobileClasses() {
    return this.isMobile ? ['p-0', 'my-n2'] : []
  }
}
</script>

<style lang="scss">
.public-navigation-menu {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 75px;
  z-index: 5;
  a.nav-link {
    color: inherit !important;
  }
}
</style>
